import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Fab,
  Stack,
  TextField,
  TextareaAutosize,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  RestartAlt as RestartAltIcon,
  VolumeOff,
  VolumeUp,
  Explore,
  ExploreOff,
} from "@mui/icons-material";
import * as THREE from "three";
import * as CANNON from "cannon";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { AppSingleton } from "../services/user_config";
import { Fireworks } from "fireworks-js";
import {
  createRoomAPI,
  getFaiChunsAPI,
  resetFaiChunsAPI,
} from "../services/backend";
import { EmojiContainer } from "../components/emoji-container";
import { v4 as uuidv4 } from "uuid";

const isDebug = process.env.REACT_APP_IS_DEBUG === "true";
console.log("isDebug");
console.log(isDebug);

const MirrorModel = ({
  modelRef,
  rendererRef,
  canvasRef,
  sceneRef,
  cameraRef,
  controlsRef,
  initializedCompleted,
}) => {
  console.log("mirrorModel");
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(
    75, // MARK: fov (field of view)
    window.innerWidth / window.innerHeight, // MARK: aspect (aspect ratio)
    0.1, // MARK: near (near clipping plane)
    1000 // MARK: far (far clipping plane)
  );

  // const renderer = new THREE.WebGLRenderer({ antialias: true });
  // renderer.setSize(window.innerWidth, window.innerHeight);
  // document.body.appendChild(renderer.domElement);

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  // renderer.setSize(window.innerWidth * 0.98, window.innerHeight * 0.92);
  renderer.setSize(window.innerWidth, window.innerHeight);
  const canvasElement = canvasRef.current;
  while (canvasElement.firstChild) {
    canvasElement.removeChild(canvasElement.firstChild);
  }
  canvasElement.appendChild(renderer.domElement);

  // const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
  // scene.add(ambientLight);

  // const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
  // directionalLight.position.set(1.0, 1.0, 1.0).normalize();
  // scene.add(directionalLight);

  const loader = new GLTFLoader();
  loader.load(
    // "../models/the_orange_tree/scene.gltf",
    "../models/the_orange_tree/bls_w_009.gltf",
    (gltf) => {
      console.log("Model Init");
      renderer.render(scene, camera);
      modelRef.current = gltf.scene;
      camera.position.y = 0.4;
      camera.position.z = 0.8;

      // Get the bounding box of the model
      const bbox = new THREE.Box3().setFromObject(modelRef.current);
      // Get the size (dimensions) of the model
      const size = new THREE.Vector3();
      bbox.getSize(size);
      console.log("size");
      console.log(size);
      // Get the position of the model
      const position = new THREE.Vector3();
      bbox.getCenter(position);
      console.log("position");
      console.log(position);

      scene.add(gltf.scene);
      rendererRef.current = renderer;
      sceneRef.current = scene;
      cameraRef.current = camera;
      rendererRef.current.render(sceneRef.current, cameraRef.current);

      // controlsRef.current = new OrbitControls(
      //   cameraRef.current,
      //   rendererRef.current
      // );
      initializedCompleted();
    },
    undefined,
    (error) => {
      console.error("Error loading GLTF model:", error);
    }
  );
};

const WishingTreePage = () => {
  const { roomId } = useParams();
  const canvasRef = useRef(null);
  const modelRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const controlsRef = useRef();
  const cameraRef = useRef(null);
  const zoomRatePrevRef = useRef(0.5);
  const zoomRateRef = useRef(1.0);
  const windSpeed = 0.005; // Adjust the wind speed as needed
  const rotationSpeed = new THREE.Vector3(0.0005, 0.001, 0.0005); // Adjust the texture offset speed as needed
  const geometryRefs = useRef([]);
  const meshesRefs = useRef([]);
  const rotationAngleRef = useRef(0.0);
  const [faiChuns, setFaiChuns] = useState([]);
  const [faiChunLocation, setFaiChunLocation] = useState({
    x: 0.0,
    y: 0.1,
    z: 0.0,
    r: 0.0,
  });
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const animationPausedRef = useRef(false);
  const [soundOnOff, setSoundOnOff] = useState(true);
  const [animationOnOff, setAnimationOnOff] = useState(true);
  const [animationFrameId, setAnimationFrameId] = useState(null);
  const [emojiFireDict, setEmojiFireDict] = useState({});
  const [backgroundColor, setBackgroundColor] = useState("#FFF");
  const fireworksRef = useRef(null);
  const fireworksConfigRef = useRef({
    intensity: 2,
    explosion: 5,
    gravity: 4,
    particles: 100,
    traceSpeed: 1,
    brightness: {
      min: 60,
      max: 90,
    },
    sound: {
      enabled: soundOnOff,
      files: [
        "/sounds/explosion0.mp3",
        "/sounds/explosion1.mp3",
        "/sounds/explosion2.mp3",
      ],
      volume: {
        min: 50,
        max: 70,
      },
    },
  });

  const appSingleton = new AppSingleton();

  // let animationFrameId = null;
  const animate = () => {
    if (sceneRef.current) {
      if (zoomRateRef.current !== zoomRatePrevRef.current) {
        cameraRef.current.position.z = zoomRateRef.current;
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
      // rotationAngle += 0.002;
      if (animationOnOff) {
        rotationAngleRef.current = rotationAngleRef.current + 0.002;
      }
      // sceneRef.current.rotation.y = rotationAngle;
      sceneRef.current.rotation.y = rotationAngleRef.current;
      rendererRef.current.render(sceneRef.current, cameraRef.current);

      if (!animationPausedRef.current) {
        // animationFrameId = requestAnimationFrame(animate);
        setAnimationFrameId(requestAnimationFrame(animate));
      }

      // Apply wind force to the object's position
      // meshesRefs.current.forEach((boxMesh) => {
      //   boxMesh.rotation.x += rotationSpeed.x;
      //   boxMesh.rotation.y += rotationSpeed.y;
      //   boxMesh.rotation.z += rotationSpeed.z;
      // });

      // Render the scene
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    }
  };
  const stopAnimate = () => {
    // console.log("animationFrameId");
    // console.log(animationFrameId);
    cancelAnimationFrame(animationFrameId);
    setAnimationFrameId(null);
    // animationFrameId = null;
  };

  useEffect(() => {
    const initializedCompleted = () => {
      setBackgroundColor("#B0D6F5");
      animate();
      getFaiChunsAPI({ roomId }).then(({ data }) => {
        // console.log("existing faichuns");
        // console.log(data?.fai_chuns);
        data?.fai_chuns.forEach((el) => {
          // console.log("existing faichun");
          // console.log(el);
          addObject({
            x: el?.coordinate?.x,
            y: el?.coordinate?.y,
            z: el?.coordinate?.z,
            rotationAngle: el.coordinate?.rotation_angle,
            data_url: el?.data_url,
          });
        });
      });
    };
    MirrorModel({
      modelRef,
      rendererRef,
      canvasRef,
      sceneRef,
      cameraRef,
      controlsRef,
      initializedCompleted,
    });
    appSingleton.faiChunCallbackDict["random"] = ({ coordinate, data_url }) => {
      console.log("App Container receive data_url");
      // console.log(data_url);
      // addObject({ x: ran(), y: 0.3, z: ran(), data_url });
      fireworksRef.current?.launch();
      setTimeout(() => {
        addObject({
          x: coordinate.x,
          y: coordinate.y,
          z: coordinate.z,
          rotationAngle: coordinate.rotation_angle,
          data_url,
        });
      }, 1800);
    };
    appSingleton.faiChunResetCallbackDict["random"] = () => {
      resetFaiChuns();
    };
    appSingleton.emojiCallback["random"] = ({ emoji_key }) => {
      setEmojiFireDict({ id: uuidv4(), key: emoji_key });
    };
    fireworksRef.current = new Fireworks(
      document.getElementById("fireworks-container"),
      fireworksConfigRef.current
    );
    // MARK: Just in case user directly type in the link
    createRoomAPI({ roomId }).then(({ data }) => {});
  }, []);

  useEffect(() => {
    updateFireworskConfig({
      newConfig: {
        sound: {
          ...fireworksConfigRef.current.sound,
          enabled: soundOnOff,
        },
      },
    });
  }, [soundOnOff]);

  useEffect(() => {
    animationOnOff ? animate() : stopAnimate();
  }, [animationOnOff]);

  const updateFireworskConfig = ({ newConfig }) => {
    fireworksConfigRef.current = {
      ...fireworksConfigRef.current,
      ...newConfig,
    };
    fireworksRef.current.updateOptions(fireworksConfigRef.current);
  };

  const addObject = ({
    x = 0.0,
    y = 0.0,
    z = 0.0,
    rotationAngle = 0.0,
    data_url,
  } = {}) => {
    const textureLoader = new THREE.TextureLoader();
    const texture = textureLoader.load(
      data_url ?? "../fai-chun/templates/心想事成.png",
      () => {
        console.log(`Adding Object\tx:${x}\ty:${y}\tz:${z}`);
        // console.log(`adding object, data_url: ${data_url}`);
        // const boxHeight = 0.1;
        // const boxGeometry = new THREE.BoxGeometry(0.03, boxHeight, 0.002); // MARK: scale to this scene
        const boxGeometry = new THREE.BoxGeometry(
          (105.0 / 2970.0) * 1.5,
          (297.0 / 2970.0) * 1.5,
          0.002
        );
        const materials = [
          new THREE.MeshBasicMaterial({ color: 0xde4d44, materialIndex: 0 }), // Right side
          new THREE.MeshBasicMaterial({ color: 0xde4d44, materialIndex: 0 }), // Left side
          new THREE.MeshBasicMaterial({ color: 0xde4d44, materialIndex: 0 }), // Top side
          new THREE.MeshBasicMaterial({ color: 0xde4d44, materialIndex: 0 }), // Bottom side
          new THREE.MeshBasicMaterial({
            map: texture,
            // color: 0xde4d44,
            materialIndex: 1,
          }), // Front side
          new THREE.MeshBasicMaterial({ color: 0xde4d44, materialIndex: 0 }), // Back side
        ];
        const boxMesh = new THREE.Mesh(boxGeometry, materials);
        // boxMesh.position.set(ran(), 0.1, ran());
        boxMesh.position.set(x, y, z);
        boxMesh.rotation.set(
          0.0,
          // THREE.MathUtils.degToRad(ran({ min: 0, max: 90 })),
          THREE.MathUtils.degToRad(360 - rotationAngle + 90),
          0.0
        );
        // Add the box mesh to the scene
        sceneRef.current.add(boxMesh);
        meshesRefs.current.push(boxMesh);
        rendererRef.current.render(sceneRef.current, cameraRef.current);
        console.log("added red paper");
      }
    );
  };

  const resetFaiChuns = () => {
    meshesRefs.current.forEach((_mesh) => {
      sceneRef.current.remove(_mesh);
    });
    meshesRefs.current = [];
  };

  const onMouseDown = ({ clientX, clientY }) => {
    animationPausedRef.current = true;
    setDragging(true);
    setStartPosition({ x: clientX, y: clientY });
    stopAnimate();
  };
  const onMouseMove = ({ clientX, clientY }) => {
    if (dragging) {
      const deltaX = clientX - startPosition.x;
      const deltaY = clientY - startPosition.y;
      sceneRef.current.rotation.y += deltaX * 0.001;
      rotationAngleRef.current = sceneRef.current.rotation.y;
      rendererRef.current.render(sceneRef.current, cameraRef.current);
      setPosition((prevPosition) => ({
        x: prevPosition.x + deltaX,
        y: prevPosition.y + deltaY,
      }));
      setStartPosition({ x: clientX, y: clientY });
    }
  };
  const onMouseUp = () => {
    setDragging(false);
    animationPausedRef.current = false;
    if (animationOnOff) {
      animate();
    }
  };

  return (
    <Box>
      <Box
        sx={{
          position: "absolute",
          bottom: 16,
          width: "95%",
          zIndex: 9999,
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <EmojiContainer emojiFireDict={emojiFireDict} />
      </Box>
      <Box
        id="fireworks-container"
        sx={{
          position: "absolute",
          // zIndex: -1,
          top: 0,
          left: 0,
          width: "90%",
          height: "90%",
          pointerEvents: "none",
        }}
      ></Box>
      {isDebug ? (
        <Box>
          <TextField
            value={faiChunLocation.x}
            onChange={(evt) =>
              setFaiChunLocation({ ...faiChunLocation, x: evt.target.value })
            }
          >
            X
          </TextField>
          <TextField
            value={faiChunLocation.y}
            onChange={(evt) =>
              setFaiChunLocation({ ...faiChunLocation, y: evt.target.value })
            }
          >
            Y
          </TextField>
          <TextField
            value={faiChunLocation.z}
            onChange={(evt) =>
              setFaiChunLocation({ ...faiChunLocation, z: evt.target.value })
            }
          >
            Z
          </TextField>
          <TextField
            value={faiChunLocation.r}
            onChange={(evt) =>
              setFaiChunLocation({ ...faiChunLocation, r: evt.target.value })
            }
          >
            Rotation
          </TextField>
          <Button
            onClick={() =>
              addObject({
                x: faiChunLocation.x,
                y: faiChunLocation.y,
                z: faiChunLocation.z,
                rotationAngle: faiChunLocation.r,
              })
            }
          >
            Add Red Paper
          </Button>
        </Box>
      ) : null}
      <Stack spacing={4} sx={{ margin: 4, position: "absolute" }}>
        <Tooltip title="放大">
          <Fab
            color="white"
            disabled={!animationOnOff}
            onClick={() => {
              zoomRatePrevRef.current = zoomRateRef.current;
              zoomRateRef.current -= 0.1;
            }}
          >
            <AddIcon />
          </Fab>
        </Tooltip>
        <Tooltip title="拉遠">
          <Fab
            color="white"
            disabled={!animationOnOff}
            onClick={() => {
              zoomRatePrevRef.current = zoomRateRef.current;
              zoomRateRef.current += 0.1;
            }}
          >
            <RemoveIcon />
          </Fab>
        </Tooltip>
      </Stack>
      <Stack
        spacing={4}
        sx={{
          margin: 4,
          right: 4,
          display: "flex",
          alignItems: "flex-end",
          position: "absolute",
        }}
      >
        <Tooltip title="點擊複製房間編號">
          <Chip
            label={`房間編號: ${roomId}`}
            color="primary"
            onClick={() => {
              navigator.clipboard.writeText(roomId);
              appSingleton.setNotifications([
                ...appSingleton.notifications,
                {
                  data: `已複製房間編號: ${roomId}`,
                },
              ]);
            }}
          />
        </Tooltip>
        <Tooltip title="點擊開啟聲音">
          <Fab
            color="white"
            onClick={() => {
              setSoundOnOff(!soundOnOff);
            }}
          >
            {soundOnOff ? <VolumeUp /> : <VolumeOff />}
          </Fab>
        </Tooltip>
        <Tooltip title="點擊開啟/關閉展示模式">
          <Fab
            color="white"
            onClick={() => {
              setAnimationOnOff(!animationOnOff);
            }}
          >
            {animationOnOff ? <Explore /> : <ExploreOff />}
          </Fab>
        </Tooltip>
        <Tooltip title="重置揮春">
          <Fab
            color="white"
            onClick={() => {
              // MARK: API to reset fai chun
              resetFaiChunsAPI({ roomId }).then(({ data }) => {
                // console.log("resetFaiChunsAPI");
                // console.log(data);
                if (data?.result === 1) {
                  resetFaiChuns();
                }
              });
            }}
          >
            <RestartAltIcon />
          </Fab>
        </Tooltip>
      </Stack>
      <Box
        sx={{
          position: "absolute",
          display: "flex",
          justifyContent: "center",
          zIndex: -1,
          // width: window.innerWidth,
          // height: window.innerHeight,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <img src={"/background/circle-loading.gif"} alt="" />
      </Box>
      <div
        ref={canvasRef}
        onMouseDown={(evt) => {
          onMouseDown({ clientX: evt.clientX, clientY: evt.clientY });
        }}
        onMouseMove={(evt) => {
          onMouseMove({ clientX: evt.clientX, clientY: evt.clientY });
        }}
        onMouseUp={() => onMouseUp()}
        onTouchStart={(evt) =>
          onMouseDown({
            clientX: evt.changedTouches[0].clientX,
            clientY: evt.changedTouches[0].clientY,
          })
        }
        onTouchMove={(evt) =>
          onMouseMove({
            clientX: evt.changedTouches[0].clientX,
            clientY: evt.changedTouches[0].clientY,
          })
        }
        onTouchEnd={() => onMouseUp()}
        // style={{ width: window.innerWidth, height: window.innerHeight }}
      />
    </Box>
  );
};

const f = () => {};

export { WishingTreePage, f };
